import React, { useEffect } from 'react';
import './index.scss';
import { useUsers } from '@/Recoil/User';
import { useSession } from '@/Recoil/Session/Session';
import { navigateTo } from '@/Utils/routing';
import { useKoreanStudyType } from '@/Recoil/PersonaTest';
import IsometricRoom from '@/Components/IsometricRoom/IsometricRoom';
import { useOrnament, usePlacement } from '@/Recoil/World';
import { logBehavior } from '@/Data/Behavior';
import { useKoreanStudyTypeContext } from '../KoreanStudyTypeContext';

const personalityTypes = {
    'idol_stan': {
        name: 'Si Idol Stan',
        badge: '/img/type_si_idol_stan.svg',
        descriptions: [
            { emoji: '🩷', text: 'Target cuma satu: biar makin jago ngedukung idol!' },
            { emoji: '🔥', text: 'Ngefans = belajar. Bayangin bias aja udah bisa bikin begadang belajar Korea.' },
            { emoji: '🥹', text: 'Suatu hari pengen bilang "saranghaeyo!" langsung ke bias, makanya belajar terus!' }
        ]
    },
    'k_content_lover': {
        name: 'K-content lover',
        badge: '/img/type_k_content_lover.svg',
        descriptions: [
            { emoji: '🍿', text: 'Belajar hari ini: nonton drama sampai tamat.' },
            { emoji: '👀', text: 'Lirik OST = PR bahasa Korea hari ini.' },
            { emoji: '💥', text: 'Makin ditonton, makin jago. Kamu pemenang sejati dalam belajar Bahasa Korea!' }
        ]
    },
    'social_butterfly': {
        name: 'Social Butterfly',
        badge: '/img/type_social_butterfly.svg',
        descriptions: [
            { emoji: '✨', text: 'Percaya kalau semakin banyak jumlah teman orang Korea, level bahasa Korea juga meningkat' },
            { emoji: '😆', text: 'Salah? Bodo amat—yang penting ngomong dulu!' },
            { emoji: '💬', text: 'Denger "Wah, pengucapan kamu bagus!" dari orang Korea = motivasi naik drastis' }
        ]
    },
    'travel_preparer': {
        name: 'Si Calon Traveler',
        badge: '/img/type_si_calon_traveler.svg',
        descriptions: [
            { emoji: '🏝️', text: 'Ngimpi bisa ngobrol lancar pas liburan ke Korea' },
            { emoji: '🇰🇷', text: 'Dari bandara udah siap praktek bahasa Korea beneran.' },
            { emoji: '📍', text: 'Nyasar? Santai. Yang penting bisa nanya pakai bahasa Korea!' }
        ]
    },
    'aesthetic_learner': {
        name: 'Si Paling Estetik',
        badge: '/img/type_si_paling_estetik.svg',
        descriptions: [
            { emoji: '🎀', text: 'Begitu coba pakai bahasa Korea, mood langsung jadi lebih emosional' },
            { emoji: '🫧', text: 'Satu stiker, satu kalimat—semua harus Korea banget' },
            { emoji: '📓', text: 'Lagi ngeisi hidupnya pelan-pelan dengan vibes Korea.' }
        ]
    },
    'skill_hunter': {
        name: 'Skill Hunter',
        badge: '/img/type_skill_hunter.svg',
        descriptions: [
            { emoji: '🎯', text: 'Fokus ke level yang lebih tinggi, tiap hari ngasah skill.' },
            { emoji: '📈', text: 'Liat nilai naik tuh rasanya nagih banget.' },
            { emoji: '💡', text: 'Percaya kalau usaha hari ini bisa jadi peluang besar buat besok.' }
        ]
    },
    'academic_learner': {
        name: 'Si Akademis',
        badge: '/img/type_si_akademis.svg',
        descriptions: [
            { emoji: '🔍', text: 'Harus paham konsep sampai tuntas biar hati tenang.' },
            { emoji: '🧠', text: 'Kalau grammar dan struktur udah jelas, hidup terasa lebih teratur.' },
            { emoji: '✍️', text: 'Harus ngerti 100% baru bisa lanjut ke step berikutnya.' }
        ]
    },
    'casual_learner': {
        name: 'Casual Learner',
        badge: '/img/type_casual_learner.svg',
        descriptions: [
            { emoji: '😎', text: 'Mulai cuma karena penasaran… eh ternyata seru juga belajar Korea.' },
            { emoji: '🕺', text: 'Rajin? Hmm… tergantung mood. Tapi kalau lagi seru, langsung all-in.' },
            { emoji: '🥨', text: 'Tipe pembelajar yang bebas—mood belajar dateng kapan aja tetap nggak canggung.' }
        ]
    }
};

type PersonalityType = keyof typeof personalityTypes;

const KoreanStudyTypePreview = ({ path }: {path?: string}) => {
    const { session } = useSession();
    const { profiles, syncProfiles } = useUsers();
    const { results, fetchResult } = useKoreanStudyType();
    const { addressid, typeid, characterPollingStatus } = useKoreanStudyTypeContext();
    const { fetchOrnaments } = useOrnament();
    const { fetchPlacements } = usePlacement();

    const pathParts = path?.split('/').filter(Boolean) ?? [];
    const pathAddressid = pathParts[0] || addressid || session.addressid;

    useEffect(() => {
        if (pathAddressid) {
            syncProfiles([pathAddressid]);
            fetchResult(pathAddressid);
            fetchPlacements(pathAddressid);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathAddressid]);

    useEffect(() => {
        fetchOrnaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const profile = profiles[pathAddressid];
    const result = results[pathAddressid];
    const username = profile?.name || 'Kamu';
    const currentType: PersonalityType = result?.type ?? typeid ?? 'casual_learner';
    const typeData = personalityTypes[currentType];

    const handleGenerateRoom = () => {
        logBehavior('personatest:251212kst:button_press:generate_room_from_preview');
        navigateTo(`/personatest/koreanstudytype/makeavatar/${pathAddressid}/${currentType}`);
    };

    const isGeneratingCharacter = characterPollingStatus === 'polling';

    return (
        <div className="korean-study-type-preview-container">
            {/* Top Section */}
            <div className="top-section">
                <div className="logo-wrapper">
                    <div className="logo-icon">
                        <img src="/img/matelogoguide.png" alt="MATE Logo" />
                    </div>
                    <span className="logo-text">{"MATE"}</span>
                </div>
            </div>

            {/* Header Section */}
            <div className="header-section">
                <p className="subtitle">{`Kepribadian Bahasa Korea ${username} adalah...`}</p>
                <div className="type-badge">
                    <div className="badge-icon">
                        <img src={typeData.badge} alt={typeData.name} />
                    </div>
                    <span className="badge-text">{typeData.name}</span>
                </div>
            </div>

            {/* Room Preview */}
            <div className="room-preview-section">
                <div className="room-container">
                    <IsometricRoom addressid={pathAddressid} />
                    <div className="silhouette-overlay">
                        <img
                            src={'https://static.koreanmate.app/personatest/koreanstudytype/character/silluet.png'}
                            alt="Character Silhouette"
                            className={isGeneratingCharacter ? 'generating' : ''}
                        />
                        {isGeneratingCharacter && (
                            <div className="generating-indicator">
                                <div className="spinner" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Description Card */}
            <div className="description-section">
                <div className="description-card">
                    {typeData.descriptions.map((item, index) => (
                        <div className="description-item" key={index}>
                            <span className="emoji">{item.emoji}</span>
                            <p>{item.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
                <div className="cta-wrapper">
                    <button className="btn-generate" onClick={handleGenerateRoom}>
                        {"Buat kamarku"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KoreanStudyTypePreview;
